/**
 * Simple Markdown to HTML Converter
 *
 * Converts basic markdown syntax to HTML for use in BlockNote custom blocks.
 * Supports: bold, italic, links, line breaks, paragraphs
 *
 * Security: Output should be passed through sanitizeHtml() before rendering
 *
 * Usage:
 *   import { markdownToHtml } from '../../utils/markdown';
 *   const html = markdownToHtml('**bold** and *italic*');
 */

/**
 * Convert markdown text to HTML
 *
 * Supported syntax:
 * - **bold** or __bold__ → <strong>bold</strong>
 * - *italic* or _italic_ → <em>italic</em>
 * - [link text](url) → <a href="url">link text</a>
 * - Double line breaks → <p> tags
 * - Single line breaks → <br> tags
 * - Bullet lists (- item or * item at start of line) → <ul><li>
 * - Numbered lists (1. item at start of line) → <ol><li>
 *
 * @param {string} markdown - The markdown text to convert
 * @returns {string} HTML string
 */
export function markdownToHtml(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  let html = markdown;

  // First, process lists before paragraph conversion
  // Split into blocks separated by double newlines
  const blocks = html.split(/\n\n+/);
  const processedBlocks = blocks.map(block => {
    if (!block.trim()) return '';

    // Check if this block is a list (all lines start with - or * or numbered)
    const lines = block.split('\n');
    const isUnorderedList = lines.every(line => /^\s*[-*]\s+/.test(line) || !line.trim());
    const isOrderedList = lines.every(line => /^\s*\d+\.\s+/.test(line) || !line.trim());

    if (isUnorderedList && lines.some(line => /^\s*[-*]\s+/.test(line))) {
      // Convert unordered list
      const listItems = lines
        .filter(line => /^\s*[-*]\s+/.test(line))
        .map(line => {
          const content = line.replace(/^\s*[-*]\s+/, '').trim();
          return `<li>${content}</li>`;
        })
        .join('');
      return `<ul>${listItems}</ul>`;
    } else if (isOrderedList && lines.some(line => /^\s*\d+\.\s+/.test(line))) {
      // Convert ordered list
      const listItems = lines
        .filter(line => /^\s*\d+\.\s+/.test(line))
        .map(line => {
          const content = line.replace(/^\s*\d+\.\s+/, '').trim();
          return `<li>${content}</li>`;
        })
        .join('');
      return `<ol>${listItems}</ol>`;
    } else {
      // Regular paragraph
      return `<p>${block.trim()}</p>`;
    }
  }).filter(Boolean);

  html = processedBlocks.join('\n');

  // Convert links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Convert bold **text** or __text__
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Convert italic *text* or _text_ (but not list markers at start of line)
  // Use callback to check if match is at line start (Safari <16.4 doesn't support lookbehind)
  html = html.replace(/\*([^*\n]+)\*/g, (match, content, offset, string) => {
    // Skip if at start of string or after newline (would be a list marker)
    if (offset === 0 || string[offset - 1] === '\n') {
      return match;
    }
    return `<em>${content}</em>`;
  });
  html = html.replace(/_([^_\n]+)_/g, (match, content, offset, string) => {
    // Skip if at start of string or after newline
    if (offset === 0 || string[offset - 1] === '\n') {
      return match;
    }
    return `<em>${content}</em>`;
  });

  // Convert single line breaks to <br> (within paragraphs only, not in lists)
  html = html.replace(/<p>(.*?)<\/p>/gs, (match, content) => {
    const withBreaks = content.replace(/\n/g, '<br>');
    return `<p>${withBreaks}</p>`;
  });

  return html;
}

/**
 * Preview markdown as plain text (for character counting, etc.)
 * Strips markdown syntax to show approximate plain text length
 *
 * @param {string} markdown - The markdown text
 * @returns {string} Plain text without markdown syntax
 */
export function markdownToPlainText(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  let text = markdown;

  // Remove links but keep link text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove bold markers
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');

  // Remove italic markers
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');

  return text;
}

/**
 * Check if text contains markdown syntax
 *
 * @param {string} text - The text to check
 * @returns {boolean} True if markdown syntax is detected
 */
export function hasMarkdownSyntax(text) {
  if (!text || typeof text !== 'string') {
    return false;
  }

  const patterns = [
    /\*\*[^*]+\*\*/,  // Bold **
    /__[^_]+__/,      // Bold __
    /\*[^*]+\*/,      // Italic *
    /_[^_]+_/,        // Italic _
    /\[.+\]\(.+\)/,   // Links
  ];

  return patterns.some(pattern => pattern.test(text));
}

/**
 * Convert HTML back to markdown
 * Used when editing existing content that was already converted to HTML
 *
 * @param {string} html - The HTML to convert
 * @returns {string} Markdown text
 */
export function htmlToMarkdown(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let markdown = html;

  // Convert <strong> and <b> to **bold**
  markdown = markdown.replace(/<strong>(.*?)<\/strong>/g, '**$1**');
  markdown = markdown.replace(/<b>(.*?)<\/b>/g, '**$1**');

  // Convert <em> and <i> to *italic*
  markdown = markdown.replace(/<em>(.*?)<\/em>/g, '*$1*');
  markdown = markdown.replace(/<i>(.*?)<\/i>/g, '*$1*');

  // Convert <a> to [text](url)
  // Handles href with double quotes, single quotes, or no quotes
  // Also handles attributes in any order and is case-insensitive
  markdown = markdown.replace(/<a\s+(?:[^>]*?\s+)?href=(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*?>(.*?)<\/a>/gi, (match, doubleQuoted, singleQuoted, unquoted, text) => {
    const url = doubleQuoted || singleQuoted || unquoted;
    return `[${text}](${url})`;
  });

  // Convert <br> to newlines
  markdown = markdown.replace(/<br\s*\/?>/g, '\n');

  // Convert unordered lists <ul><li>...</li></ul> to - item
  markdown = markdown.replace(/<ul>(.*?)<\/ul>/gs, (match, content) => {
    const items = [];
    const liRegex = /<li>(.*?)<\/li>/gs;
    let liMatch;
    while ((liMatch = liRegex.exec(content)) !== null) {
      items.push(`- ${liMatch[1].trim()}`);
    }
    return items.join('\n');
  });

  // Convert ordered lists <ol><li>...</li></ol> to 1. item
  markdown = markdown.replace(/<ol>(.*?)<\/ol>/gs, (match, content) => {
    const items = [];
    const liRegex = /<li>(.*?)<\/li>/gs;
    let liMatch;
    let index = 1;
    while ((liMatch = liRegex.exec(content)) !== null) {
      items.push(`${index}. ${liMatch[1].trim()}`);
      index++;
    }
    return items.join('\n');
  });

  // Convert paragraphs - extract content and add double newlines between them
  const paragraphs = [];
  const pRegex = /<p>(.*?)<\/p>/gs;
  let match;
  while ((match = pRegex.exec(markdown)) !== null) {
    paragraphs.push(match[1]);
  }

  if (paragraphs.length > 0) {
    // Check if there's content outside paragraphs (like lists)
    const outsideParagraphs = markdown.replace(/<p>.*?<\/p>/gs, '').trim();
    if (outsideParagraphs) {
      // Mix of paragraphs and other elements - reconstruct in order
      markdown = markdown.replace(/<p>(.*?)<\/p>/gs, '$1\n\n');
    } else {
      markdown = paragraphs.join('\n\n');
    }
  } else {
    // No paragraph tags, just clean up any remaining tags
    markdown = markdown.replace(/<\/?p>/g, '');
  }

  // Clean up any remaining list tags that might not have been processed
  markdown = markdown.replace(/<\/?ul>/g, '');
  markdown = markdown.replace(/<\/?ol>/g, '');
  markdown = markdown.replace(/<\/?li>/g, '');

  // Decode all HTML entities (named, numeric, hex)
  // Use DOMParser in browser, fallback to manual decode in Node.js
  markdown = decodeHtmlEntities(markdown);

  // Clean up extra whitespace while preserving intentional double newlines
  markdown = markdown.replace(/\n{3,}/g, '\n\n');

  return markdown.trim();
}

/**
 * Decode HTML entities in a string
 * Works in both browser and Node.js environments
 *
 * @param {string} text - Text containing HTML entities
 * @returns {string} Decoded text
 */
function decodeHtmlEntities(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Browser environment - use DOMParser for robust decoding
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.documentElement.textContent || '';
  }

  // Node.js environment - manual decode of common entities
  // This handles the most common cases without requiring external dependencies
  // Using Unicode escapes to avoid encoding issues
  const entityMap = {
    '&nbsp;': '\u00A0',      // non-breaking space
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&mdash;': '\u2014',     // em dash
    '&ndash;': '\u2013',     // en dash
    '&hellip;': '\u2026',    // horizontal ellipsis
    '&rsquo;': '\u2019',     // right single quotation mark
    '&lsquo;': '\u2018',     // left single quotation mark
    '&rdquo;': '\u201D',     // right double quotation mark
    '&ldquo;': '\u201C',     // left double quotation mark
    '&trade;': '\u2122',     // trade mark sign
    '&copy;': '\u00A9',      // copyright sign
    '&reg;': '\u00AE',       // registered sign
    '&bull;': '\u2022',      // bullet
    '&middot;': '\u00B7',    // middle dot
    '&deg;': '\u00B0',       // degree sign
    '&plusmn;': '\u00B1',    // plus-minus sign
    '&times;': '\u00D7',     // multiplication sign
    '&divide;': '\u00F7',    // division sign
    '&frac12;': '\u00BD',    // vulgar fraction one half
    '&frac14;': '\u00BC',    // vulgar fraction one quarter
    '&frac34;': '\u00BE',    // vulgar fraction three quarters
    '&pound;': '\u00A3',     // pound sign
    '&euro;': '\u20AC',      // euro sign
    '&yen;': '\u00A5'        // yen sign
  };

  let decoded = text;

  // Replace named entities
  for (const [entity, char] of Object.entries(entityMap)) {
    decoded = decoded.replace(new RegExp(entity, 'g'), char);
  }

  // Replace numeric entities (&#123; and &#xAB;)
  decoded = decoded.replace(/&#(\d+);/g, (match, dec) => {
    return String.fromCharCode(parseInt(dec, 10));
  });

  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });

  return decoded;
}
